import React, { useEffect, useRef, useState } from 'react';
import classNames from 'clsx';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import type {
  ILanguageSelectorLayoutProps,
  IItem,
} from '../LanguageSelector.types';
import { testIds } from '../constants';
import semanticClassNames from '../LanguageSelector.semanticClassNames';
import {
  LanguageSelectorDropDownHandle,
  useOrganizeItems,
  Option,
} from './LanguageSelectorDropDownCommon';
import style from './style/LanguageSelector.scss';
import { KeyboardHandler } from './KeyboardHandler';
import { getAriaLabel } from './common/AriaLabelGetter';
import { useDropdownHasSpace } from './hooks/useDropdownHasSpace';
import { useClickOutside } from '../../../providers/useClickOutside/useClickOutside';

export const LanguageSelectorDropdown = ({
  id,
  translations: { defaultAriaLabel },
  items,
  onChange,
  extraOptions,
  isOpen = false,
  setIsOpen,
  disabled,
  customClassNames,
}: ILanguageSelectorLayoutProps) => {
  const [itemIndex, setItemIndex] = useState(0);
  const [shouldOpenUp, listRef] = useDropdownHasSpace(isOpen);
  const handlerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement>>([]);

  const options = useOrganizeItems(items);

  const ariaLabel = getAriaLabel(defaultAriaLabel!, options[0].label);

  const navigateUp = () => {
    const newIndex = itemIndex === 1 ? options.length - 1 : itemIndex - 1;
    setItemIndex(newIndex);
    itemRefs.current[newIndex]?.focus();
  };

  const navigateDown = () => {
    const newIndex = itemIndex === options.length - 1 ? 1 : itemIndex + 1;
    setItemIndex(newIndex);
    itemRefs.current[newIndex]?.focus();
  };

  const onClickArrowDownKey = () => {
    if (!isOpen) {
      setIsOpen(true);
      return;
    }

    if (shouldOpenUp) {
      navigateUp();
    } else {
      navigateDown();
    }
  };

  const onClickArrowUp = () => {
    if (!isOpen) {
      setIsOpen(true);
      return;
    }

    if (shouldOpenUp) {
      navigateDown();
    } else {
      navigateUp();
    }
  };

  const onReturnOrSpace = () => {
    if (itemIndex) {
      onChange(options[itemIndex]?.value);
      closeDropdown();
    } else {
      setIsOpen(!isOpen);
    }
  };

  const onClickEscape = () => {
    closeDropdown();
    handlerRef.current?.focus();
  };

  const onHomeKey = () => {
    if (isOpen) {
      const newIndex = 1;
      setItemIndex(newIndex);
      itemRefs.current[newIndex]?.focus();
    }
  };

  const onEndKey = () => {
    if (isOpen) {
      const newIndex = options.length - 1;
      setItemIndex(newIndex);
      itemRefs.current[newIndex]?.focus();
    }
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setItemIndex(0);
  };

  useClickOutside([handlerRef, listRef], closeDropdown, true);

  const onClick = () => {
    if (isOpen) {
      setItemIndex(shouldOpenUp ? options.length - 1 : 0);
    } else {
      updateHeight();
    }
    setIsOpen(!isOpen);
  };

  const getOptionClickHandler = (index: number, item: IItem) => () => {
    return index > 0 ? onChange(item.value) : onClick();
  };

  const updateHeight = (): void => {
    if (!handlerRef.current) {
      return;
    }
    handlerRef.current.style.setProperty(
      '--height',
      `${handlerRef.current.offsetHeight}px`,
    );
  };

  const scrollViewAfterKeyboardNavigation = () => {
    if (itemIndex > 0) {
      itemRefs.current[itemIndex]?.scrollIntoView?.({
        block: 'nearest',
      });
    }
  };

  useEffect(updateHeight, []);
  useEffect(scrollViewAfterKeyboardNavigation, [itemIndex]);

  return (
    <KeyboardHandler
      tabIndex={0}
      aria-haspopup="true"
      aria-expanded={isOpen}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      data-testid={testIds.container}
      onArrowKeyDown={onClickArrowDownKey}
      onArrowKeyUp={onClickArrowUp}
      onEscape={onClickEscape}
      onReturnOrSpace={onReturnOrSpace}
      onHome={onHomeKey}
      onEnd={onEndKey}
      className={style.container}
      ref={handlerRef}
      role="button"
    >
      <LanguageSelectorDropDownHandle
        onClick={onClick}
        item={options[0]}
        className={classNames({
          [style.open]: isOpen,
        })}
        showArrow={!!extraOptions.showArrow}
        showText={true}
        customClassNames={customClassNames}
      >
        <div
          role="menu"
          className={classNames([style.list], { [style.up]: shouldOpenUp })}
          ref={listRef}
        >
          {options.map((item, index) => {
            return (
              <Option
                ref={el => {
                  if (el) {
                    itemRefs.current[index] = el;
                  }
                }}
                id={`${id}-dropdown-option-${index}`}
                key={item.value}
                item={item}
                aria-label={item.label}
                showArrow={index === 0}
                role="menuitem"
                onClick={getOptionClickHandler(index, item)}
                className={classNames([
                  style.item,
                  formatClassNames(semanticClassNames.option),
                  {
                    [style.focus]: index === itemIndex,
                    [style.selected]: index === 0,
                  },
                ])}
                data-testid={`dropdown-option-${item.value}`}
              />
            );
          })}
        </div>
      </LanguageSelectorDropDownHandle>
    </KeyboardHandler>
  );
};
